import React, { createContext, MutableRefObject, useContext } from 'react';

import { noop } from 'lodash';
import { PatientForm, PatientFormTemplate } from 'requests/resources';

import { PatientFormTemplateItem, PatientFormTemplateLumaBotFlow, Setting } from '@lib/resources/types';

import { TMessageWrapper } from '../containers/Messages/MessageWrapper';

export interface ChatContextProps {
  settings: Setting['lumabot'];
  flowTemplates?: PatientFormTemplate[];
  flowId?: string;
  loading?: boolean;
  typing?: boolean;
  currentFlowTemplate?: PatientFormTemplate;
  currentFlow?: PatientForm;
  currentItem?: PatientFormTemplateItem;
  messages: TMessageWrapper[];
  isLiveChatEnabled?: boolean;
  mode: 'default' | 'blocked';
  inputRef: MutableRefObject<HTMLElement | null>;
  variant?: 'backend';
  isPopper?: boolean;
  setMode: (mode: 'default' | 'blocked') => void;
  pushFlowTemplate: (lumaBotFlow: PatientFormTemplateLumaBotFlow, text?: string) => void;
  pushAnswer: (message: Partial<TMessageWrapper>, opts?: { skipNextItem?: boolean; customDelay?: number }) => void;
  revertMessage: (message: TMessageWrapper) => void;
  clear: () => void;
}

export const chatContextDefaultValue: ChatContextProps = {
  settings: {},
  flowTemplates: [],
  loading: false,
  typing: false,
  currentFlow: undefined,
  currentItem: undefined,
  messages: [],
  isLiveChatEnabled: false,
  mode: 'default',
  inputRef: { current: null },
  setMode: noop,
  pushFlowTemplate: noop,
  pushAnswer: noop,
  revertMessage: noop,
  clear: noop,
};

const ChatContext = createContext<ChatContextProps>(chatContextDefaultValue);

export const ChatProvider: React.FC<ChatContextProps> = ({ children, ...rest }) => (
  <ChatContext.Provider value={rest}>{children}</ChatContext.Provider>
);

export const useChatContext = () => {
  const ctx = useContext(ChatContext);
  return ctx;
};

if (process.env.NODE_ENV !== 'production') {
  ChatContext.displayName = 'LumaChatContext';
}

export default ChatContext;
